import { Sparkles, Briefcase, Layers, ShieldCheck, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/hero-realestate.webp";
import logoBranco from "@/assets/logo-permuta-branco.webp";
import GuidedSwapWizard from "@/components/GuidedSwapWizard";
import AudienceToggle from "@/components/AudienceToggle";
import VideoExplainer from "@/components/VideoExplainer";
import { useAudience } from "@/hooks/useAudience";

const HeroSection = () => {
  const { audience } = useAudience();

  return (
    <section className="relative min-h-[82vh] flex items-center pt-24 pb-8 overflow-hidden bg-gradient-hero">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Vista aérea de bairro residencial brasileiro"
          width={1600}
          height={1050}
          {...({ fetchpriority: "high" } as any)}
          decoding="async"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--brand-navy-deep))]/95 via-[hsl(var(--brand-navy))]/85 to-[hsl(var(--brand-navy))]/40" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[hsl(var(--brand-teal))]/20 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-4 flex justify-center lg:justify-start">
          <AudienceToggle variant="dark" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          {/* Left copy */}
          <div>
            <motion.img
              src={logoBranco}
              alt="Permuta Digital"
              width={800}
              height={533}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="h-32 md:h-40 lg:h-48 w-auto object-contain mb-3 drop-shadow-[0_4px_20px_rgba(0,0,0,0.4)]"
              loading="eager"
            />

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-dark text-white text-xs font-display font-medium mb-4 border border-white/10"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--brand-teal-bright))] animate-pulse" />
              Inteligência de permuta para proprietários e corretores em SP
            </motion.div>

            <AnimatePresence mode="wait">
              {audience === "owner" ? (
                <motion.div
                  key="owner-copy"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white leading-[1.08] mb-4">
                    Troque seu imóvel
                    <br />
                    <span className="text-gradient-teal">com Inteligência Artificial.</span>
                  </h1>
                  <p className="text-base md:text-lg text-white/80 mb-5 max-w-md font-body leading-snug">
                    Nosso algoritmo cruza milhares de imóveis automaticamente, identifica matches perfeitos e viabiliza permutas em cadeia com segurança e inteligência.
                  </p>
                  <div className="flex items-center gap-2 text-[11px] text-white/80 font-body">
                    <Sparkles size={12} className="text-[hsl(var(--brand-teal-bright))] shrink-0" />
                    Negociação assistida — corretores e parceiros podem conduzir sua operação
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="broker-copy"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white leading-[1.08] mb-4">
                    Sua carteira parada
                    <br />
                    <span className="text-gradient-teal">vale mais com IA.</span>
                  </h1>
                  <p className="text-base md:text-lg text-white/80 mb-5 max-w-md font-body leading-snug">
                    A Permuta Digital é o motor de inteligência da sua operação. Você no controle do cliente; nós encontramos as permutas que você não enxergaria sozinho.
                  </p>
                  <div className="flex flex-wrap gap-3 mb-2">
                    <Link to="/corretores">
                      <Button variant="accent" size="lg" className="gap-2">
                        Conhecer o cockpit do corretor <ArrowRight size={18} />
                      </Button>
                    </Link>
                    <Link to="/signup?role=broker">
                      <Button variant="outline" size="lg" className="bg-white/5 text-white border-white/30 hover:bg-white/15 hover:text-white">
                        Cadastrar como corretor
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: changes per audience */}
          <div className="lg:justify-self-end w-full max-w-md lg:max-w-lg">
            <AnimatePresence mode="wait">
              {audience === "owner" ? (
                <motion.div
                  key="owner-right"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col gap-4"
                >
                  <VideoExplainer
                    videoId="dhKuHCWOdHY"
                    title="Veja como funciona"
                    description="Passo a passo para proprietários"
                  />
                  <GuidedSwapWizard />
                </motion.div>
              ) : (
                <motion.div
                  key="broker-right"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/15 p-6 md:p-8"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Briefcase className="text-[hsl(var(--brand-teal-bright))]" size={20} />
                    <span className="text-white/80 font-display font-medium text-sm">Para corretores e imobiliárias</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-5">
                    O que muda na sua operação
                  </h2>
                  <ul className="space-y-3">
                    {[
                      { icon: Layers, title: "Cockpit do corretor", desc: "Cadastre carteira, clientes e visualize matches em um só lugar." },
                      { icon: Sparkles, title: "Matching automático", desc: "A IA cruza sua carteira com milhares de imóveis 24/7." },
                      { icon: ShieldCheck, title: "Comissão protegida", desc: "Taxa reduzida de 0,9% e rastreabilidade em cada operação." },
                    ].map((item) => (
                      <li key={item.title} className="flex items-start gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                        <item.icon className="text-accent shrink-0 mt-0.5" size={20} />
                        <div>
                          <div className="text-white font-display font-semibold text-sm">{item.title}</div>
                          <div className="text-white/70 font-body text-xs leading-relaxed">{item.desc}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;